import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './dashboard-home.scss';
import Testimonial2 from './Testimonial2';
import Footer from './Footer';
import PendingActionsBar from './PendingActionsBar';
import FrontLoader from '../layout/FrontLoader';
import awardIcon from '../../Assets/images/award.svg';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import useMakeRequest from '../../utils/ApiHelper';
import { ApiConstants } from '../../constants/ApiConstants';
import { ApiTypes } from '../../constants/Constants';
import { LanguageConstants } from '../../constants/LanguageConstants';
import { encodeID } from '../../utils/Helper';
import { useScrollToHash } from '../../utils/useScrollToHash';
const pub = `${process.env.PUBLIC_URL || ''}`;

type DashboardHeroSlide = {
  slideKey: string;
  bannerId: string;
  title: string;
  desc: string;
  bg: string;
  bannerUrl: string | null;
};

const NO_BANNER_TEXT = 'No banner for the category you selected';

function resolveBannerUrl(raw: unknown): string | null {
  const value = String(raw ?? '').trim();
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
  } catch {
    return null;
  }
  return null;
}

/** Static slides when API returns no banners (same as before). */
const DEFAULT_HERO_SLIDES: DashboardHeroSlide[] = [
  {
    slideKey: 'default-0',
    bannerId: 'default-0',
    title: 'WELCOME TO QUOTE N BUY',
    desc:
      'Quote N Buy connects you with trusted global suppliers to help you source products quickly, securely, and with confidence.',
    bg: `${pub}/home/banner-1.webp`,
    bannerUrl: null,
  },
  {
    slideKey: 'default-1',
    bannerId: 'default-1',
    title: 'SOURCE WITH CONFIDENCE',
    desc:
      'Request quotes, compare offers, and approve orders — all in one place with transparent pricing and reliable delivery.',
    bg: `${pub}/home/banner-2.webp`,
    bannerUrl: null,
  },
  {
    slideKey: 'default-2',
    bannerId: 'default-2',
    title: 'GLOBAL REACH, LOCAL SERVICE',
    desc:
      'We help you find the right parts and products from verified suppliers worldwide, tailored to your timeline and budget.',
    bg: `${pub}/home/banner-3.webp`,
    bannerUrl: null,
  },
  {
    slideKey: 'default-3',
    bannerId: 'default-3',
    title: 'YOUR PROCUREMENT PARTNER',
    desc:
      'From specialized components to everyday supplies, Quote N Buy streamlines sourcing so you can focus on what matters.',
    bg: `${pub}/home/banner-4.webp`,
    bannerUrl: null,
  },
];

function mapApiBannersToHeroSlides(banners: any[] | undefined | null): DashboardHeroSlide[] {
  if (!Array.isArray(banners) || banners.length === 0) return [];

  return banners
    .map((row: any, index: number) => {
      const details = row?.banner_details || {};
      const rawName = (details?.name ?? '').toString().trim();
      const lines = rawName
        .split(/\r?\n/)
        .map((l: string) => l.trim())
        .filter(Boolean);
      const title = lines[0] || `Banner ${index + 1}`;
      const apiDesc = (details?.description ?? '').toString().trim();
      const desc = apiDesc || (lines.length > 1 ? lines.slice(1).join(' ') : '');
      const imagePath = row?.banner_image_details?.file_path;
      const bg =
        imagePath && String(imagePath).trim() !== ''
          ? String(imagePath).trim()
          : DEFAULT_HERO_SLIDES[index % DEFAULT_HERO_SLIDES.length].bg;
      const bannerId = String(row?.id ?? `banner-${index}`);

      return {
        slideKey: `${bannerId}-${index}`,
        bannerId,
        title,
        desc,
        bg,
        bannerUrl: resolveBannerUrl(details?.banner_url),
      };
    })
    .filter((s) => s.title);
}

function buildBannerGroupCopy(
  slides: DashboardHeroSlide[],
): Map<string, Pick<DashboardHeroSlide, 'title' | 'desc' | 'bannerUrl'>> {
  const map = new Map<string, Pick<DashboardHeroSlide, 'title' | 'desc' | 'bannerUrl'>>();
  for (const slide of slides) {
    if (!map.has(slide.bannerId)) {
      map.set(slide.bannerId, {
        title: slide.title,
        desc: slide.desc,
        bannerUrl: slide.bannerUrl,
      });
    }
  }
  return map;
}

/** Max visible hero dots; when more slides exist, window slides and keeps active centered when possible. */
const MAX_VISIBLE_HERO_DOTS = 5;

function getHeroDotWindow(activeIndex: number, total: number): number[] {
  if (total <= 0) return [];
  if (total <= MAX_VISIBLE_HERO_DOTS) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const mid = Math.floor(MAX_VISIBLE_HERO_DOTS / 2);
  let start = activeIndex - mid;
  if (start < 0) start = 0;
  if (start > total - MAX_VISIBLE_HERO_DOTS) {
    start = total - MAX_VISIBLE_HERO_DOTS;
  }
  return Array.from({ length: MAX_VISIBLE_HERO_DOTS }, (_, i) => start + i);
}

type TrendingProduct = {
  id: number;
  category: string;
  product: string;
  description: string;
  image: string;
};

const SKELETON_TRENDING_CARDS = [0, 1, 2, 3];

/** Placeholder layout shown under the frosted loader while homepage data loads. */
function DashboardHomeSkeleton() {
  return (
    <div className="dashboard-home-skeleton" aria-hidden="true">
      <section className="dashboard-home-skeleton__hero">
        <div className="container">
          <div className="dashboard-home-skeleton__hero-inner">
            <span className="dashboard-home-skeleton__line dashboard-home-skeleton__line--title" />
            <span className="dashboard-home-skeleton__line dashboard-home-skeleton__line--desc" />
            <span className="dashboard-home-skeleton__line dashboard-home-skeleton__line--desc dashboard-home-skeleton__line--short" />
          </div>
        </div>
      </section>
      <section className="dashboard-home-skeleton__trending mb-5">
        <div className="container">
          <div className="dashboard-trending-wrap sec-bg">
            <span className="dashboard-home-skeleton__head" />
            <div className="dashboard-home-skeleton__cards">
              {SKELETON_TRENDING_CARDS.map((key) => (
                <span key={key} className="dashboard-home-skeleton__card" />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function DashboardHome() {
  const navigate = useNavigate();
  const HomeApi = useMakeRequest();
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [activeBannerGroupId, setActiveBannerGroupId] = useState('');
  const heroSwiperRef = useRef<SwiperType | null>(null);
  const trendingSwiperRef = useRef<SwiperType | null>(null);
  const [trendSwiperNav, setTrendSwiperNav] = useState({
    isBeginning: true,
    isEnd: false,
    isLocked: true,
  });

  const syncTrendSwiperNav = useCallback((swiper: SwiperType) => {
    setTrendSwiperNav({
      isBeginning: swiper.isBeginning,
      isEnd: swiper.isEnd,
      isLocked: swiper.isLocked,
    });
  }, []);

  const scrollTrend = useCallback((direction: 'left' | 'right') => {
    const swiper = trendingSwiperRef.current;
    if (!swiper || swiper.isLocked) return;
    if (direction === 'left') {
      swiper.slidePrev();
    } else {
      swiper.slideNext();
    }
  }, []);

  /** Request finished (success or handled failure); until then show full-page loader — no default hero/trending flash. */
  const homeFetchSettled =
    !HomeApi.loading && (HomeApi.status === true || HomeApi.error != null);
  const showHomeLoader = !homeFetchSettled;
  useScrollToHash({ enabled: !showHomeLoader });

  useEffect(() => {
    document.documentElement.classList.remove('qnb-home-boot');
  }, []);

  useEffect(() => {
    HomeApi.makeRequest({
      url: ApiConstants?.Homepage,
      type: ApiTypes.POST,
      toastStatus: false,
    });
  }, []);

  const heroSlides = useMemo((): DashboardHeroSlide[] => {
    if (!homeFetchSettled) return [];
    if (HomeApi.status === true) {
      const fromApi = mapApiBannersToHeroSlides(HomeApi?.data?.data?.banners);
      return fromApi;
    }
    return DEFAULT_HERO_SLIDES;
  }, [homeFetchSettled, HomeApi.status, HomeApi.data, HomeApi.error]);

  const bannerGroupCopy = useMemo(() => buildBannerGroupCopy(heroSlides), [heroSlides]);

  useEffect(() => {
    if (!heroSlides.length) return;
    heroSlides.forEach((slide) => {
      const img = new Image();
      img.src = slide.bg;
    });
  }, [heroSlides]);

  useEffect(() => {
    if (!heroSlides.length) return;
    setActiveHeroSlide(0);
    setActiveBannerGroupId(heroSlides[0]?.bannerId ?? '');
  }, [heroSlides]);

  const handleHeroSlideChange = useCallback(
    (swiper: SwiperType) => {
      const safeIndex = Math.min(
        Math.max(swiper.realIndex, 0),
        Math.max(heroSlides.length - 1, 0),
      );
      const slide = heroSlides[safeIndex];
      if (!slide) return;
      setActiveHeroSlide(safeIndex);
      setActiveBannerGroupId((prev) => (prev === slide.bannerId ? prev : slide.bannerId));
    },
    [heroSlides],
  );

  const trendingProducts = useMemo((): TrendingProduct[] => {
    if (!homeFetchSettled || HomeApi.status !== true) return [];
    const productList = HomeApi?.data?.data?.products;
    if (!Array.isArray(productList) || productList.length === 0) return [];

    return productList
      .map((item: any, index: number) => {
        const details = item?.product_details || {};
        const imagePath = item?.product_image_details?.file_path;
        const name = (details?.name || '').toString().trim();
        const description = (details?.description || '').toString().trim();
        if (!name) return null;

        return {
          id: Number(item?.id || index + 1),
          category: (item?.category_name || item?.category?.name || 'Products').toString(),
          product: name,
          description,
          image: imagePath || `${pub}/home/trending-pro${(index % 4) + 1}.png`,
        };
      })
      .filter(Boolean) as TrendingProduct[];
  }, [homeFetchSettled, HomeApi.status, HomeApi.data]);

  useEffect(() => {
    const swiper = trendingSwiperRef.current;
    if (!swiper) return;
    requestAnimationFrame(() => {
      swiper.update();
      syncTrendSwiperNav(swiper);
    });
  }, [trendingProducts, syncTrendSwiperNav]);

  const hasProductsResponse = homeFetchSettled && HomeApi.status === true;
  const showNoBannerState = hasProductsResponse && heroSlides.length === 0;
  const safeHeroIndex =
    heroSlides.length > 0 ? Math.min(activeHeroSlide, heroSlides.length - 1) : 0;
  const visibleHeroDotIndices = useMemo(
    () => getHeroDotWindow(safeHeroIndex, heroSlides.length),
    [safeHeroIndex, heroSlides.length],
  );
  const activeBannerCopy = bannerGroupCopy.get(activeBannerGroupId);
  const activeBannerUrl = activeBannerCopy?.bannerUrl ?? null;

  const goToHeroSlide = useCallback((index: number) => {
    const swiper = heroSwiperRef.current;
    if (!swiper) return;
    if (swiper.params.loop) {
      swiper.slideToLoop(index);
    } else {
      swiper.slideTo(index);
    }
  }, []);

  const handleHeroBannerClick = useCallback(
    (event?: React.MouseEvent | React.KeyboardEvent) => {
      if (
        event &&
        'target' in event &&
        event.target instanceof HTMLElement &&
        event.target.closest('.dashboard-hero__pagination, .swiper-pagination')
      ) {
        return;
      }
      const url = resolveBannerUrl(activeBannerUrl);
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [activeBannerUrl],
  );

  const handleHeroBannerKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handleHeroBannerClick(event);
    },
    [handleHeroBannerClick],
  );

  const handleTrendingProductClick = (productId: number) => {
    if (!productId) return;
    navigate(`/productdetail/${encodeID(productId)}`);
  };

  /** Skeleton + frosted `FrontLoader` overlay while homepage data loads (SS1 pattern). */
  return (
    <div
      className={`dashboard-home page-upper-space page-loading-shell${showHomeLoader ? ' page-loading-shell--loading dashboard-home--loading' : ''}`}
      aria-busy={showHomeLoader}
    >
      {showHomeLoader ? <FrontLoader /> : null}
      {showHomeLoader ? (
        <DashboardHomeSkeleton />
      ) : (
        <div className="page-loading-shell__content dashboard-home__content">
      <PendingActionsBar />
      <section
        id="dashboard-hero"
        className={`dashboard-hero${showNoBannerState ? ' dashboard-hero--empty' : ''}${
          activeBannerUrl ? ' dashboard-hero--clickable' : ''
        }`}
        onClick={activeBannerUrl ? handleHeroBannerClick : undefined}
        onKeyDown={activeBannerUrl ? handleHeroBannerKeyDown : undefined}
        role={activeBannerUrl ? 'link' : undefined}
        tabIndex={activeBannerUrl ? 0 : undefined}
        aria-label={activeBannerUrl ? activeBannerCopy?.title : undefined}
      >
        <div className="container">
          {showNoBannerState ? (
            <div className="dashboard-hero__empty">
              <p className="dashboard-hero__empty-text text-white">{NO_BANNER_TEXT}</p>
            </div>
          ) : (
            <>
              {heroSlides.map((slideItem, index) => (
                <div
                  key={slideItem.slideKey}
                  className={`dashboard-hero__bg${
                    safeHeroIndex === index ? ' dashboard-hero__bg--active' : ''
                  }`}
                  aria-hidden
                  style={{
                    backgroundImage: `url(${slideItem.bg})`,
                  }}
                />
              ))}
              <div className="dashboard-hero__overlay" style={{zIndex: 1}} aria-hidden />
              <Swiper
                key={heroSlides.map((s) => s.slideKey).join('-')}
                className="dashboard-hero__swiper"
                slidesPerView={1}
                loop={heroSlides.length > 1}
                speed={850}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                spaceBetween={20}
                modules={[Autoplay]}
                onSwiper={(swiper: SwiperType) => {
                  heroSwiperRef.current = swiper;
                }}
                onSlideChange={handleHeroSlideChange}
              >
                {heroSlides.map((slideItem) => {
                  const copy = bannerGroupCopy.get(slideItem.bannerId) ?? slideItem;

                  return (
                    <SwiperSlide key={slideItem.slideKey}>
                      <div className="dashboard-hero__inner">
                        <h1 key={activeBannerGroupId} className="dashboard-hero__title">
                          {copy.title}
                        </h1>
                        {copy.desc ? (
                          <p key={`${activeBannerGroupId}-desc`} className="dashboard-hero__desc">
                            {copy.desc}
                          </p>
                        ) : null}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
              {heroSlides.length > 1 ? (
                <div
                  className="dashboard-hero__pagination swiper-pagination swiper-pagination-clickable swiper-pagination-bullets"
                  role="tablist"
                  aria-label="Banner slides"
                >
                  {visibleHeroDotIndices.map((slideIndex) => {
                    const isActive = slideIndex === safeHeroIndex;
                    return (
                      <button
                        key={`hero-dot-${slideIndex}`}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-label={`Go to slide ${slideIndex + 1}`}
                        className={`swiper-pagination-bullet${
                          isActive ? ' swiper-pagination-bullet-active' : ''
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          goToHeroSlide(slideIndex);
                        }}
                      />
                    );
                  })}
                </div>
              ) : null}
            </>
          )}

        </div>
      </section>

      <section id="dashboard-trending" className="dashboard-trending mb-5">
        <div className='container'>
          <div className='dashboard-trending-wrap sec-bg'>
            <div className="dashboard-trending__head d-flex align-items-center justify-content-between">
              <h2 className="sec-ttl text-capitalize text-start mb-0">{LanguageConstants?.DASH_TRENDING_TITLE}</h2>
              {trendingProducts.length > 4 ? (
                <Link to="/product" className="see-more-btn">
                  {LanguageConstants?.DASH_SEE_MORE}
                </Link>
              ) : null}
            </div>
            <div className="dashboard-trending__row-wrap">
              {trendingProducts.length > 0 ? (
                <Swiper
                  key={trendingProducts.map((p) => p.id).join('-')}
                  className="dashboard-trending__row"
                  spaceBetween={15}
                  speed={550}
                  slidesPerView={1.1}
                  breakpoints={{
                    576: { slidesPerView: 2 },
                    768: { slidesPerView: 3 },
                    1200: { slidesPerView: 4, spaceBetween: 30 },
                  }}
                  onSwiper={(swiper: SwiperType) => {
                    trendingSwiperRef.current = swiper;
                    syncTrendSwiperNav(swiper);
                  }}
                  onSlideChange={syncTrendSwiperNav}
                  onResize={syncTrendSwiperNav}
                  onBreakpoint={syncTrendSwiperNav}
                >
                  {trendingProducts.map((item) => (
                    <SwiperSlide key={item.id}>
                      <article
                        className="dashboard-trend-card"
                        onClick={() => handleTrendingProductClick(item.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleTrendingProductClick(item.id);
                          }
                        }}
                      >
                        <div className="dashboard-trend-card__visual">
                          <span className="dashboard-trend-card__badge">
                            <img src={awardIcon} className="dashboard-trend-card__badge-icon"/>
                            <span className="dashboard-trend-card__badge-text">{LanguageConstants?.DASH_MOST_POPULAR}</span>
                          </span>
                          {item.image ? (
                          <img
                            className="dashboard-trend-card__img"
                            src={item.image}
                            alt=""
                            loading="lazy"
                          />
                          ) : null}

                          <div className="dashboard-trend-card__bottom">
                            <p className="dashboard-trend-card__cat">{item.product}</p>
                            <h3 className="dashboard-trend-card__name">{item.description || item.category}</h3>
                          </div>
                        </div>
                      </article>
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : hasProductsResponse ? (
                <p className="dashboard-trending__empty text-center mb-0">{LanguageConstants?.DASH_NO_PRODUCTS}</p>
              ) : null}
            </div>
            {!trendSwiperNav.isLocked && trendingProducts.length > 0 ? (
            <div className="dashboard-trending__nav">
              <button
                type="button"
                className="dashboard-trending__nav-btn"
                aria-label="Scroll trending left"
                disabled={trendSwiperNav.isBeginning}
                onClick={() => scrollTrend('left')}
              >
                <i className="icon-chev-right"></i>
              </button>
              <button
                type="button"
                className="dashboard-trending__nav-btn"
                aria-label="Scroll trending right"
                disabled={trendSwiperNav.isEnd}
                onClick={() => scrollTrend('right')}
              >
                <i className="icon-chev-right"></i>
              </button>
            </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* <LandingReviewsSection /> */}
      <Testimonial2 />

      {/* <LandingFooter /> */}
      <Footer />
        </div>
      )}
    </div>
  );
}
